import React, { useEffect, useRef, useState } from 'react';
import { useSocket } from './hooks/useSocket';
import { useGameSession } from './hooks/useGameSession';
import { useSound } from './hooks/useSound';
import { useToast } from './components/Toast';
import { GameBoard } from './components/GameBoard';
import { ScoreBoard } from './components/ScoreBoard';
import { RetroGrid } from './components/RetroGrid';
import { NeonButton } from './components/NeonButton';
import { SoundToggle } from './components/SoundToggle';
import { MobileOptimizer } from './components/MobileOptimizer';
import { Lobby } from './components/Lobby';
import { ChatPanel } from './components/ChatPanel';
import { Logo } from './components/Logo';
import { GameSession, PlayerInfo } from './types/game';
import {
  GameResyncPayload,
  GameStartPayload,
  GameUpdatePayload,
  RateLimitPayload,
  ReconnectWindowPayload
} from './types/socket';
import { LocalStorageService } from './services/localStorageService';
import { ThemeService } from './services/themeService';

// Initialize theme on app load
ThemeService.applyTheme(ThemeService.getCurrentTheme());

const App: React.FC = () => {
  const { socket, isConnected, connectionStatus } = useSocket();
  const { playSound } = useSound();
  const { showToast } = useToast();
  const [isInGame, setIsInGame] = useState(false);
  const [autoJoined, setAutoJoined] = useState(false);
  const [reconnectSeconds, setReconnectSeconds] = useState(0);
  const [reconnectWindowMs, setReconnectWindowMs] = useState(30000);
  const [reconnectDisabled, setReconnectDisabled] = useState(false);
  const [roundEndCountdown, setRoundEndCountdown] = useState(0);
  const [sessionEndFading, setSessionEndFading] = useState(false);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [rematchStatus, setRematchStatus] = useState<{ accepted: number; required: number } | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const _gameSession = useGameSession(socket);
  const {
    session,
    player,
    opponent,
    error,
    makeMove,
    nextRound,
    resetGame,
    setSession,
    setPlayer,
    setOpponent,
    setError
  } = _gameSession;
  const sessionRef = useRef<GameSession | null>(null);
  const playerRef = useRef<PlayerInfo | null>(null);
  const disconnectTimeoutRef = useRef<number | null>(null);

  // Migración de schema al montar la app (una sola vez)
  useEffect(() => {
    LocalStorageService.migrateIfNeeded();
  }, []);

  const handleBackToLobby = () => {
    if (socket && socket.connected && sessionRef.current?.id && sessionRef.current?.status === 'session_end') {
      socket.emit('cancel-rematch', { sessionId: sessionRef.current.id });
    }
    LocalStorageService.clearActiveSession();
    setIsInGame(false);
    resetGame();
    setRematchRequested(false);
    setRematchStatus(null);
  };

  useEffect(() => {
    if (!socket || !isConnected) return;

    const activeSession = LocalStorageService.getActiveSession();
    if (activeSession?.sessionId && activeSession.playerToken) {
      socket.emit('reconnect-session', {
        sessionId: activeSession.sessionId,
        playerToken: activeSession.playerToken
      });
      return;
    }
    if (activeSession?.sessionId && !activeSession.playerToken) {
      LocalStorageService.clearActiveSession();
    }

    const prefs = LocalStorageService.getPlayerPreferences();
    const savedName = LocalStorageService.getPlayerName();

    if (prefs.autoConnect && savedName && !autoJoined) {
      setAutoJoined(true);
      socket.emit('join-lobby', savedName);
    }
  }, [socket, isConnected, autoJoined]);

  useEffect(() => {
    if (!isConnected) {
      setAutoJoined(false);
    }
  }, [isConnected]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    playerRef.current = player;
  }, [player]);

  useEffect(() => {
    if (session?.status !== 'session_end') {
      setRematchRequested(false);
      setRematchStatus(null);
    }
  }, [session?.status]);

  // Cuenta atrás para auto-avance de ronda
  useEffect(() => {
    const deadline = session?.roundEndDeadline;
    if (!deadline) {
      setRoundEndCountdown(0);
      return;
    }
    const update = () => setRoundEndCountdown(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    update();
    const interval = window.setInterval(update, 500);
    return () => window.clearInterval(interval);
  }, [session?.roundEndDeadline]);

  useEffect(() => {
    if (!socket) return;

    socket.on('game-start', ({ session, player, opponent, playerToken }: GameStartPayload) => {
      console.log('🎮 Evento game-start recibido:', { session, player, opponent });
      playSound('connect');
      showToast(`¡Partida iniciada contra ${opponent.name}!`, 'success');
      setRematchRequested(false);
      setRematchStatus(null);
      setSessionEndFading(false);
      sessionRef.current = session;
      playerRef.current = player;
      if (typeof playerToken === 'string' && playerToken) {
        LocalStorageService.setActiveSession(session.id, player.name, playerToken);
        setReconnectDisabled(false);
      } else {
        LocalStorageService.clearActiveSession();
        setReconnectDisabled(true);
        showToast('No se pudo guardar la sesión. Reconexión deshabilitada.', 'warning');
      }
      setSession(session);
      setPlayer(player);
      setOpponent(opponent);
      setIsInGame(true);
      console.log('✅ Estado actualizado, isInGame = true');
    });

    socket.on('game-resync', ({ session, player, opponent, playerToken }: GameResyncPayload) => {
      sessionRef.current = session;
      playerRef.current = player;
      setRematchRequested(false);
      setRematchStatus(null);
      setSessionEndFading(false);
      const storedToken = LocalStorageService.getActiveSession()?.playerToken;
      const nextToken = typeof playerToken === 'string' && playerToken ? playerToken : storedToken;
      if (nextToken) {
        LocalStorageService.setActiveSession(session.id, player.name, nextToken);
        setReconnectDisabled(false);
      } else {
        LocalStorageService.clearActiveSession();
        setReconnectDisabled(true);
        showToast('Token de sesión no disponible. Reconexión deshabilitada.', 'warning');
      }
      setSession(session);
      setPlayer(player);
      setOpponent(opponent);
      setIsInGame(true);
      showToast('✅ Reconectado a la partida', 'success');
    });

    socket.on('game-update', (updatedSession: GameUpdatePayload) => {
      const prevStatus = sessionRef.current?.status;
      const newStatus = updatedSession.status;
      const gameJustEnded = newStatus === 'session_end' && prevStatus !== 'session_end';
      const currentPlayer = playerRef.current;

      if (gameJustEnded && currentPlayer) {
        const playerWon = updatedSession.sessionWinner === currentPlayer.symbol;
        const isTie = updatedSession.sessionWinner === null;

        let result: 'win' | 'loss' | 'tie';
        if (isTie) {
          result = 'tie';
        } else if (playerWon) {
          result = 'win';
        } else {
          result = 'loss';
        }

        const opponentPlayer = updatedSession.players.find(p => p.id !== currentPlayer.id);
        const opponentName = opponentPlayer?.name || 'Desconocido';

        LocalStorageService.saveGameResult(
          opponentName,
          currentPlayer.symbol,
          updatedSession.scores[currentPlayer.symbol],
          updatedSession.scores[currentPlayer.symbol === 'X' ? 'O' : 'X'],
          result,
          0
        );
      }

      if (newStatus === 'round_end' && prevStatus === 'playing') {
        if (updatedSession.winner === 'tie') {
          playSound('tie');
          showToast('¡Empate en esta ronda!', 'warning');
        } else if (updatedSession.winner === currentPlayer?.symbol) {
          showToast('¡Ganaste esta ronda!', 'success');
        } else {
          showToast('Perdiste esta ronda', 'error');
        }
      }
      
      if (newStatus === 'session_end') {
        if (updatedSession.sessionWinner === currentPlayer?.symbol) {
          playSound('victory');
          showToast('🎉 ¡Ganaste la partida!', 'success', 5000);
        } else {
          playSound('defeat');
          showToast('Perdiste la partida', 'error', 5000);
        }
      }
      sessionRef.current = updatedSession;
      setSession(updatedSession);
    });

    socket.on('opponent-disconnected', () => {
      showToast('Tu oponente se ha desconectado', 'warning', 5000);
      setError('Tu oponente se ha desconectado');
      if (disconnectTimeoutRef.current) {
        window.clearTimeout(disconnectTimeoutRef.current);
      }
      disconnectTimeoutRef.current = window.setTimeout(() => {
        LocalStorageService.clearActiveSession();
        resetGame();
      }, 30000);
    });

    socket.on('opponent-reconnected', () => {
      showToast('✅ Tu oponente se ha reconectado', 'success');
      setError(null);
      if (disconnectTimeoutRef.current) {
        window.clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
    });

    socket.on('reconnect-error', (message: string) => {
      showToast(message, 'error');
      LocalStorageService.clearActiveSession();
    });

    socket.on('reconnect-window', ({ windowMs }: ReconnectWindowPayload) => {
      if (typeof windowMs === 'number' && windowMs > 0) {
        setReconnectWindowMs(windowMs);
      }
    });

    socket.on('rematch-status', ({ accepted, required }: { accepted: number; required: number }) => {
      setRematchStatus({ accepted, required });
    });

    socket.on('rematch-approved', () => {
      setRematchRequested(true);
      setRematchStatus({ accepted: 2, required: 2 });
      showToast('Nueva batalla aprobada', 'success', 2000);
    });

    socket.on('rematch-expired', () => {
      setRematchRequested(false);
      setRematchStatus(null);
      showToast('Nueva batalla no aprobada a tiempo', 'warning', 3000);
    });

    socket.on('rematch-cancelled', () => {
      setRematchRequested(false);
      setRematchStatus(null);
      showToast('Nueva batalla cancelada', 'warning', 3000);
    });

    socket.on('rate-limit', ({ event, retryAfterMs }: RateLimitPayload) => {
      const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000));
      const eventMessages: Record<string, string> = {
        'send-chat-message': '¡CHAT OVERLOAD! Baja la cadencia',
        'invite-player': '¡INVITE SPAM! Respira un segundo',
        'join-lobby': '¡INSERT COIN! Espera un instante',
        'make-move': '¡TURBO MODE! Calma los inputs',
        'get-history': '¡HISTORIAL OVERLOAD!',
        'get-chat-history': '¡HISTORIAL DE CHAT OVERLOAD!',
        'get-stats': '¡STATS OVERLOAD!',
        'get-leaderboard': '¡RANKING OVERLOAD!',
        'reconnect-session': '¡RETRY COOLDOWN!',
        'request-rematch': '¡REMATCH SPAM!',
        'next-round': '¡ROUND RUSH! Más despacio'
      };
      const label = eventMessages[event] || `Demasiadas acciones (${event})`;
      showToast(`${label}. Intenta en ${seconds}s.`, 'warning');
    });

    return () => {
      socket.off('game-start');
      socket.off('game-resync');
      socket.off('game-update');
      socket.off('opponent-disconnected');
      socket.off('opponent-reconnected');
      socket.off('reconnect-error');
      socket.off('reconnect-window');
      socket.off('rematch-status');
      socket.off('rematch-approved');
      socket.off('rematch-expired');
      socket.off('rematch-cancelled');
      socket.off('rate-limit');
      if (disconnectTimeoutRef.current) {
        window.clearTimeout(disconnectTimeoutRef.current);
        disconnectTimeoutRef.current = null;
      }
    };
  }, [socket, playSound, setSession, setPlayer, setOpponent, setError, resetGame, showToast]);

  const handleCellClick = (position: number) => {
    makeMove(position);
  };

  const renderRoundEnd = () => {
    if (!session) return null;

    const winner = session.winner;
    const isPlayerWinner = winner === player?.symbol;
    const isTie = winner === 'tie';

    return (
      <div className="text-center p-8">
        <div className="mb-6">
          {isTie ? (
            <>
              <div className="text-6xl mb-4">🤝</div>
              <h2 className="font-retro text-neon-yellow text-3xl mb-2">
                ¡EMPATE!
              </h2>
              <p className="font-retro text-neon-cyan">
                Nadie ganó esta ronda
              </p>
            </>
          ) : (
            <>
              <div className="text-6xl mb-4">🏆</div>
              <h2 className={`font-retro text-3xl mb-2 ${isPlayerWinner ? 'text-neon-green' : 'text-neon-pink'}`}>
                {isPlayerWinner ? '¡GANASTE!' : '¡PERDISTE!'}
              </h2>
              <p className="font-retro text-neon-cyan">
                {winner === 'X' ? 'X' : 'O'} ha ganado esta ronda
              </p>
            </>
          )}
        </div>

        {!session.sessionWinner && (
          <div className="flex flex-col items-center gap-3">
            {roundEndCountdown > 0 && (
              <p className="font-retro text-neon-yellow text-sm animate-pulse">
                ⏱ Auto-avance en {roundEndCountdown}s
              </p>
            )}
            <NeonButton
              onClick={nextRound}
              color="cyan"
              className="text-lg"
              disabled={isBlocked}
            >
              SIGUIENTE RONDA
            </NeonButton>
          </div>
        )}
      </div>
    );
  };

  const renderSessionEnd = () => {
    if (!session || !session.sessionWinner) return null;

    const isPlayerWinner = session.sessionWinner === player?.symbol;

    const handleNewBattle = () => {
      if (!socket || !socket.connected) {
        showToast('Sin conexión: no se pudo solicitar nueva batalla', 'warning');
        return;
      }
      if (rematchRequested) return;
      setRematchRequested(true);
      setRematchStatus({ accepted: 1, required: 2 });
      socket.emit('request-rematch', { sessionId: session.id });
      showToast('Solicitud de nueva batalla enviada', 'info', 2000);
    };

    return (
      <div
        className={`text-center p-8 transition-all duration-700 ${
          sessionEndFading ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
        }`}
      >
        <div className="mb-6">
          <div className="text-8xl mb-4 animate-pulse">
            {isPlayerWinner ? '🏆' : '💀'}
          </div>
          <h1 className={`font-retro text-4xl md:text-6xl mb-4 ${isPlayerWinner ? 'text-neon-green animate-glow' : 'text-neon-pink'}`}>
            {isPlayerWinner ? '¡VICTORIA FINAL!' : '¡DERROTA!'}
          </h1>
          <p className="font-retro text-neon-cyan text-xl mb-2">
            Resultado final: {session.scores.X} - {session.scores.O}
          </p>
          <p className="font-retro text-neon-yellow">
            {session.sessionWinner === 'X' ? 'X' : 'O'} ha dominado la batalla
          </p>
        </div>

        <NeonButton
          onClick={handleNewBattle}
          color="green"
          className="text-lg"
          disabled={isBlocked || sessionEndFading || rematchRequested}
        >
          {rematchRequested
            ? `ESPERANDO ${rematchStatus?.accepted ?? 1}/${rematchStatus?.required ?? 2}`
            : 'NUEVA BATALLA'}
        </NeonButton>

        {rematchRequested && (
          <p className="font-retro text-neon-cyan text-xs mt-4">
            Esperando aprobación del rival...
          </p>
        )}
      </div>
    );
  };

  const isBlocked = connectionStatus !== 'connected';

  useEffect(() => {
    if (isInGame && isBlocked) {
      setReconnectSeconds(Math.ceil(reconnectWindowMs / 1000));
      if (reconnectTimerRef.current) {
        window.clearInterval(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = window.setInterval(() => {
        setReconnectSeconds(prev => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return;
    }

    if (reconnectTimerRef.current) {
      window.clearInterval(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setReconnectSeconds(0);
  }, [isInGame, isBlocked, reconnectWindowMs]);

  if (!isConnected && !isInGame) {
    return (
      <RetroGrid>
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <div className="w-32 h-32 md:w-48 md:h-48 mb-8">
            <Logo className="w-full h-full" />
          </div>
          <div className="text-center">
            <div className="text-6xl mb-4 animate-pulse">🔌</div>
            <p className="font-retro text-neon-cyan text-xl">
              Conectando al servidor...
            </p>
          </div>
        </div>
      </RetroGrid>
    );
  }

  if (!isInGame) {
    return <Lobby socket={socket} />;
  }

  return (
    <>
      <MobileOptimizer />
      <RetroGrid>
        <SoundToggle />
        <div className="min-h-screen flex flex-col items-center justify-center p-4 relative">
          {isInGame && isBlocked && (
            <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[9998] bg-retro-dark/90 border-2 border-neon-yellow text-neon-yellow font-retro text-xs sm:text-sm px-3 py-2 rounded-lg shadow-neon-yellow">
              {connectionStatus === 'reconnecting'
                ? `RECONectando... ${reconnectSeconds}s`
                : `SIN CONEXION - ESPERA ${reconnectSeconds}s`}
            </div>
          )}
          {isInGame && isBlocked && (
            <div className="fixed inset-0 z-[9997] bg-black/50 pointer-events-auto" />
          )}
          {!isInGame ? (
            <Lobby socket={socket} />
          ) : (
            <>
              {!reconnectDisabled && (
                <button
                  onClick={handleBackToLobby}
                  className="
                    absolute top-3 left-3 sm:top-4 sm:left-4 z-50
                    ui-button bg-retro-dark/80 border-2 
                    border-neon-cyan
                    transition-all duration-300
                    hover:scale-110 active:scale-95
                    text-neon-cyan
                  "
                >
                  ← LOBBY
                </button>
              )}

              {!session || !player || !opponent ? (
                <div className="text-center">
                  <p className="font-retro text-neon-cyan text-xl animate-pulse">
                    CARGANDO PARTIDA...
                  </p>
                </div>
              ) : (
          <>
            <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-10 md:h-10 mx-auto mb-1 sm:mb-3 md:mb-4">
              <Logo className="w-full h-full" />
            </div>
            <div className="text-center mb-4 sm:mb-6">
              <h1 className="font-retro text-xl sm:text-3xl md:text-4xl font-bold text-neon-pink mb-1 sm:mb-2">
                GATO RETRO
              </h1>
              <div className="font-retro text-neon-cyan text-[10px] sm:text-sm">
                {player.name} ({player.symbol}) VS {opponent.name} ({opponent.symbol})
              </div>
            </div>

            {error && (
              <div className="bg-retro-dark/80 border-2 border-neon-pink rounded-lg p-4 mb-4 text-center">
                <p className="font-retro text-neon-pink">{error}</p>
              </div>
            )}
            {reconnectDisabled && (
              <div className="bg-retro-dark/80 border-2 border-neon-yellow rounded-lg p-4 mb-6 text-center">
                <p className="font-retro text-neon-yellow">
                  Reconexión deshabilitada para esta sesión.
                </p>
              </div>
            )}

            {session.status === 'playing' && (
              <div className="sm:hidden w-full max-w-md mx-auto mb-3">
                <div className={`text-center font-retro text-xs border-2 rounded-lg px-3 py-2 ${
                  session.currentPlayer === player.symbol
                    ? 'border-neon-green text-neon-green bg-neon-green/10'
                    : 'border-neon-pink text-neon-pink bg-neon-pink/10'
                }`}>
                  {session.currentPlayer === player.symbol ? 'TU TURNO' : 'TURNO DEL OPONENTE'}
                </div>
              </div>
            )}

            <ScoreBoard
              session={session}
              playerSymbol={player.symbol}
              playerName={player.name}
              opponentName={opponent.name}
            />

            {session.status === 'playing' && (
              <GameBoard
                board={session.currentBoard}
                currentPlayer={session.currentPlayer}
                onCellClick={handleCellClick}
                playerSymbol={player.symbol}
                disabled={session.currentPlayer !== player.symbol || isBlocked}
                turnDeadline={session.turnDeadline}
              />
            )}

            {session.status === 'round_end' && renderRoundEnd()}

            {session.status === 'session_end' && renderSessionEnd()}

            <ChatPanel
              socket={socket}
              sessionId={session.id}
              playerId={player.id}
            />
          </>
        )}
            </>
          )}
        </div>
      </RetroGrid>
    </>
  );
};

export default App;
